import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { AlertTriangle, ShieldAlert, Info, Loader2, XCircle, CheckCircle2, BookOpen } from "lucide-react";

const ICONS = { stop: XCircle, warn: AlertTriangle, info: Info };
const COLORS = {
  stop: "hsl(var(--loss))",
  warn: "#f59e0b",
  info: "hsl(var(--ai))",
};

/**
 * PreTradeNudge — behavioral bias check modal that appears BEFORE order confirm.
 *
 * Design philosophy: Aanar's job is to WARN, not to control. Every trader must
 * be free to override — but when they do, we log it so they can look back and
 * learn. The override is a teaching moment, not a wall.
 */
export default function PreTradeNudge({ open, onOpenChange, onProceed, order }) {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acknowledged, setAcknowledged] = useState(false);

  useEffect(() => {
    if (!open || !order) return;
    setLoading(true);
    setAcknowledged(false);
    api.post("/nudges/check", order)
      .then((r) => setResult(r.data))
      .catch(() => setResult({ level: "ok", nudges: [] }))
      .finally(() => setLoading(false));
  }, [open, order]);

  // If OK, auto-proceed
  useEffect(() => {
    if (result && result.level === "ok" && open) {
      onProceed?.();
      onOpenChange(false);
    }
  }, [result, open, onProceed, onOpenChange]);

  if (!result || result.level === "ok") return null;

  const blocked = result.level === "stop";

  const logAndProceed = async (overridden) => {
    // Fire-and-forget log so we can show learning stats later
    if (overridden) {
      api.post("/nudges/override", {
        order,
        nudges: result.nudges.map((n) => n.code),
        max_severity: result.level,
      }).catch(() => { /* best effort */ });
    }
    onProceed?.();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-2xl" data-testid="nudge-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-[hsl(var(--ai))]" />
            {blocked ? "Wait — this trade needs a second look" : "Heads up before you trade"}
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="flex items-center justify-center py-10 text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Analyzing your setup…
          </div>
        ) : (
          <div className="space-y-3">
            {result.nudges.map((n, i) => {
              const Icon = ICONS[n.severity] || Info;
              const color = COLORS[n.severity] || COLORS.info;
              return (
                <div key={i} data-testid={`nudge-${n.code}`}
                     className="p-3 rounded-xl border-2"
                     style={{ borderColor: `${color}66`, backgroundColor: `${color}10` }}>
                  <div className="flex items-start gap-2.5">
                    <Icon className="w-4 h-4 mt-0.5 shrink-0" style={{ color }} />
                    <div className="flex-1">
                      <div className="font-semibold text-sm" style={{ color }}>{n.title}</div>
                      <div className="text-xs text-muted-foreground mt-1 leading-relaxed">{n.body}</div>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Explicit acknowledgment gate for stop-level nudges.
                Not a wall — a mirror.  Users must actively check the box to
                proceed, so if it works out badly they remember they chose to. */}
            {blocked && (
              <label className="flex items-start gap-2.5 p-3 rounded-xl bg-secondary/60 cursor-pointer"
                     data-testid="nudge-ack-label">
                <Checkbox
                  checked={acknowledged}
                  onCheckedChange={setAcknowledged}
                  data-testid="nudge-ack-checkbox"
                  className="mt-0.5"
                />
                <span className="text-xs text-muted-foreground leading-relaxed">
                  I've read the warnings above and want to proceed anyway.
                  I understand Aanar is <span className="text-foreground font-medium">tracking this decision</span> —
                  I can review it later in <span className="text-foreground font-medium">Journal → Overridden trades</span> to
                  see whether it worked out.
                </span>
              </label>
            )}

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-2 pt-3 border-t border-border">
              <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="nudge-cancel"
                      className="rounded-full">
                Take a break
              </Button>
              {!blocked ? (
                <Button onClick={() => logAndProceed(false)}
                        data-testid="nudge-proceed"
                        className="rounded-full">
                  <CheckCircle2 className="w-4 h-4 mr-2" /> I understand, proceed
                </Button>
              ) : (
                <Button
                  onClick={() => logAndProceed(true)}
                  disabled={!acknowledged}
                  data-testid="nudge-override"
                  className={`rounded-full font-semibold ${
                    acknowledged
                      ? "bg-[hsl(var(--loss))] hover:bg-[hsl(var(--loss))]/90 text-background"
                      : "bg-secondary text-muted-foreground cursor-not-allowed"
                  }`}>
                  Override & place trade anyway
                </Button>
              )}
            </div>

            {/* Educational one-liner - always visible for stop-level nudges */}
            {blocked && (
              <div className="flex items-center gap-2 text-[10px] text-muted-foreground pt-1">
                <BookOpen className="w-3 h-3 shrink-0" />
                <span>The pros lose too — the difference is they lose small, on purpose. That's why we ask.</span>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
